import { PostTypes } from 'constants/postTypes'
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocFromServer,
  getDocs,
  query,
  setDoc,
  where,
} from 'firebase/firestore'
import { v4 as uuidv4 } from 'uuid'

import { PostPageError } from 'utils/error/PostPageError'
import { isFile } from 'utils/files/isFile'
import { db } from 'utils/firebase'
import { getFileTypeFromMimeType, getMimeTypeAndFileFromFileOrMedia } from 'utils/media'
import { createPartialUser } from 'utils/user/createPartialUser'

import { SelectedFile } from 'types/file'
import { ImageUploadPreset, VideoUploadPreset } from 'types/fileUpload'
import { Media } from 'types/media'
import { InProgressPost, Post, PostFilter, StoredPostStep } from 'types/post'
import { Data, Options } from 'types/query'
import { TagGroup } from 'types/workoutTag'

import { uploadFile } from './files'
import { formatDocSnapshot, formatQuerySnapshot } from './formatFirestoreResults'
import { formatQueryFilters } from './formatQueryFilters'
import { formatQueryOptions } from './formatQueryOptions'
import { fetchGroupByUserId } from './groups'
import { fetchUserByUserId } from './users'

// TODO: Streamline creation and editing, maybe break things out into multiple files

const formatPostSteps = (post: InProgressPost): InProgressPost => {
  const newPost = { ...post }
  if (post.type !== PostTypes.WORKOUT) {
    newPost.steps = []
    post.steps.forEach((step) => {
      if (!!step.description || !!step.file) {
        newPost.steps.push(step)
      }
    })
  }
  return newPost
}

const uploadMedia = async (
  media: SelectedFile | Media,
  uploadPreset: { image?: ImageUploadPreset; video?: VideoUploadPreset },
  shouldReuploadMedia: boolean,
  userId: string,
  signature?: string,
  timeStamp?: string,
  publicId?: string,
): Promise<Media> => {
  if (!isFile(media) && !shouldReuploadMedia) {
    return Promise.resolve(media)
  }

  const { mediaToUpload, mediaMimeType } = getMimeTypeAndFileFromFileOrMedia(media)
  const mediaType = getFileTypeFromMimeType(mediaMimeType)

  if (mediaType === 'unknown') {
    throw new Error('Unknown media type, please select an valid media file')
  }

  const guid = uuidv4()
  const uniqueId = `${userId}/${guid}`
  const response = await uploadFile({
    file: mediaToUpload,
    fileType: mediaType,
    uploadPreset: uploadPreset[mediaType],
    uniqueId: publicId ?? uniqueId,
    signature,
    timeStamp,
  })

  const body = await response.json()

  return {
    mimeType: mediaMimeType,
    url:
      body.secure_url ??
      `https://res.cloudinary.com/upswingfitness-prod/video/upload/videos/post/${
        publicId ?? uniqueId
      }.mov`,
    _original: body,
  }
}

const prepareInProgressPost = async (
  userId: string,
  post: InProgressPost,
  isDraft: boolean,
  shouldReuploadMedia = true,
) => {
  const program = await fetchGroupByUserId({ userId })
  const user = await fetchUserByUserId({ userId })

  if (!user) throw new PostPageError('User not found, please try again', 0)

  if (!program) throw new PostPageError('Program not found on user, please try again', 0)

  if (!isDraft) {
    if (post.type === PostTypes.WORKOUT && !post.title)
      throw new PostPageError('Workout posts must have a title', 0)
    if (post.type === PostTypes.WORKOUT && !post.file)
      throw new PostPageError('Workout posts must have a thumbnail', 0)
  }

  let thumbnail: File | Media | undefined

  try {
    thumbnail = post.file
      ? await uploadMedia(post.file, { image: 'postThumbnail' }, shouldReuploadMedia, userId)
      : undefined
  } catch (error) {
    throw new PostPageError((error as Error).message, 0)
  }

  if (!isDraft && !post.combinedCategoriesAndProgramIds?.length)
    throw new PostPageError('Please select an option for `Where to Post`', 0)

  const { steps } = formatPostSteps(post)
  const stepsMedia: (Media | null)[] = []

  // Upload step media
  const stepMediaPromises = steps.map(async (step, index) => {
    const { file, isAudioEnabled, signature, timeStamp, publicId } = step

    if (!file) return Promise.resolve(null)
    try {
      return await uploadMedia(
        file,
        {
          image: 'postImage',
          video: isAudioEnabled ? 'postVideo' : 'silentPostVideo',
        },
        shouldReuploadMedia,
        userId,
        signature,
        timeStamp,
        publicId,
      )
    } catch (error) {
      return Promise.reject(new PostPageError((error as Error).message, index + 1))
    }
  })

  await Promise.all(stepMediaPromises)
    .then((resolvedStepMedia) => {
      const resolvedStepMediaFiles = resolvedStepMedia.filter(
        (value): value is Media => value !== undefined,
      )
      stepsMedia.push(...resolvedStepMediaFiles)
    })
    .catch((error: PostPageError) => {
      throw error
    })

  const computePendingStatus = (): boolean => {
    if (!post.isPending || steps.length < 1) {
      return false
    }

    if (stepsMedia.find((sm) => sm?.mimeType.includes('video'))) {
      return true
    }

    return false
  }

  const shouldSaveProgram = post.combinedCategoriesAndProgramIds?.includes('upspace') ?? false

  const viewerCategories: string[] = []
  const programIds: string[] = []
  post?.combinedCategoriesAndProgramIds?.forEach((val) => {
    if (['friends', 'followers', 'upspace'].includes(val)) {
      viewerCategories.push(val)
    } else {
      programIds.push(val)
    }
  })

  const preparedPost: Omit<
    Post,
    'id' | 'isDraft' | 'steps' | 'created' | 'updated' | 'likedCount' | 'thumbnail'
  > & { thumbnail?: Media } = {
    creator: createPartialUser(user),
    isPremium: post.visibility === 'premium',
    type: post.type,
    title: post.title,
    description: post.description ?? '',
    hasSteps: steps.length > 0,
    isPending: computePendingStatus(),
    viewerCategories,
    programIds,
  }

  if (preparedPost.type === PostTypes.ACTIVITY) {
    preparedPost.activityGroup = post.activityGroup
    preparedPost.activityType = post.activityType
  }

  if (shouldSaveProgram) {
    preparedPost.program = {
      id: program.id,
      trainer: { id: program.trainer.id },
    }
  }

  if (post.publishedToProgramIds) {
    preparedPost.publishedToProgramIds = post.publishedToProgramIds
  }

  if (post.tagGroups) {
    preparedPost.tagGroups = post.tagGroups
  }

  if (post.publishType) {
    preparedPost.publishType = post.publishType
  }

  if (thumbnail) {
    preparedPost.thumbnail = thumbnail
  }

  return { post: preparedPost, steps, stepsMedia }
}

export const createPost = async (userId: string, post: InProgressPost, isDraft = false) => {
  let inProgressPost: null | Awaited<ReturnType<typeof prepareInProgressPost>> = null

  inProgressPost = await prepareInProgressPost(userId, post, isDraft)

  if (!inProgressPost) {
    throw new PostPageError('Could not publish your post, please try again', 0)
  }
  const { post: preparedPost, steps, stepsMedia } = inProgressPost

  const newPost: Omit<Post, 'id' | 'steps' | 'thumbnail'> & {
    thumbnail?: Post['thumbnail']
  } = {
    ...preparedPost,
    created: Date.now(),
    isDraft,
    likedCount: 0,
    commentsCount: 0,
    isArchived: false,
    updated: Date.now(),
  }

  const { thumbnail } = preparedPost

  if (thumbnail) {
    newPost.thumbnail = thumbnail
  }

  const savedPost = await addDoc(collection(db, 'posts'), newPost)
  const sectionsRef = collection(db, 'posts', savedPost.id, 'sections')

  const stepPromises = steps.map(async (step, index) => {
    const newStep: StoredPostStep = {
      description: step.description || '',
      isAudioEnabled: step.isAudioEnabled ?? true,
      order: index,
    }

    if (stepsMedia[index]) {
      newStep.file = stepsMedia[index] as Media
      newStep.isPending = newStep?.file?.mimeType.includes('video')
    }

    return addDoc(sectionsRef, newStep)
  })

  await Promise.all(stepPromises)

  return savedPost?.id
}

export const editPost = async (
  userId: string,
  postId: string,
  post: InProgressPost,
  isDraft = false,
) => {
  const existingPostRef = doc(db, 'posts', postId)

  const {
    post: preparedPost,
    stepsMedia,
    steps,
  } = await prepareInProgressPost(userId, post, isDraft, false)

  const editedPost: Omit<Post, 'id' | 'steps' | 'likedCount' | 'created' | 'thumbnail'> & {
    thumbnail?: Media
  } = {
    ...preparedPost,
    isDraft,
    updated: Date.now(),
  }

  await setDoc(existingPostRef, editedPost, { merge: true })

  // Delete and recreate steps
  await deletePostSteps({ id: postId })

  const sectionsRef = collection(db, 'posts', postId, 'sections')

  const stepPromises = steps.map(async (step, index) => {
    const newStep: StoredPostStep = {
      description: step.description || '',
      file: stepsMedia[index] as Media,
      isAudioEnabled: step.isAudioEnabled ?? true,
      order: index,
    }

    return addDoc(sectionsRef, newStep)
  })

  return Promise.all(stepPromises)
}

const deletePostSteps = async (data: { id: string }) => {
  const sectionsQuery = query(collection(db, 'posts', data.id, 'sections'))
  const sectionsSnapshot = await getDocs(sectionsQuery)
  const sectionDeletionPromiseArray: Promise<unknown>[] = []

  sectionsSnapshot.forEach((section) => {
    const sectionRef = doc(db, 'posts', data.id, 'sections', section.id)
    sectionDeletionPromiseArray.push(deleteDoc(sectionRef))
  })

  return Promise.all(sectionDeletionPromiseArray)
}

export const deletePost = async (data: { id: string }) => {
  const postRef = doc(db, 'posts', data.id)

  const postSnapshot = await getDoc(postRef)
  if (!postSnapshot.exists()) {
    return
  }

  const post: Post = postSnapshot.data() as Post
  const editedPost: Post = {
    ...post,
    isArchived: true,
    updated: Date.now(),
  }

  await setDoc(postRef, editedPost, { merge: true })
}

export const fetchPost = async ({ postId }: { postId: string }): Promise<Post> => {
  if (!postId) {
    throw new Error('No data passed into fetchPostById')
  }
  const postRef = doc(db, 'posts', postId)
  const postSnapshot = await getDocFromServer(postRef)

  if (!postSnapshot.exists()) throw new Error('Post not found')

  const post = postSnapshot.data()

  const sectionsQuery = query(collection(db, 'posts', postId, 'sections'))
  const sectionsSnapshot = await getDocs(sectionsQuery)

  const postSteps: StoredPostStep[] = []

  // Get post steps
  sectionsSnapshot.forEach(async (section) => {
    postSteps.push(section.data() as StoredPostStep)
  })

  // Sort post steps based on stored order
  const orderedPostSteps = postSteps.sort((stepA, stepB) => stepA.order - stepB.order)

  const finalPost = { ...post, steps: orderedPostSteps } as Post

  const viewerCategories: string[] = finalPost.viewerCategories ?? []
  const programIds: string[] = finalPost.programIds ?? []
  finalPost.combinedCategoriesAndProgramIds = [...viewerCategories, ...programIds]
  if (!finalPost.commentsCount) {
    finalPost.commentsCount = 0
  }

  return finalPost
}

export const fetchPosts = async (
  {
    creatorId,
    premium,
    shouldIncludeDrafts,
    filter,
  }: {
    creatorId?: string | string[]
    premium?: boolean
    shouldIncludeDrafts?: boolean
    filter?: PostFilter
  },
  options: Options<Post> = {
    orderDirection: 'desc',
  },
): Promise<Array<Post>> => {
  let baseQuery = query(collection(db, 'posts'))

  if (creatorId) {
    if (Array.isArray(creatorId) && creatorId.length) {
      // Firestore only allows us to pass up to 10 values in an 'in' clause
      const firstTenCreators = creatorId.slice(0, 9)
      baseQuery = query(baseQuery, where('creator.id', 'in', firstTenCreators))
    } else if (typeof creatorId === 'string') {
      baseQuery = query(baseQuery, where('creator.id', '==', creatorId))
    } else {
      return []
    }
  }

  if (premium) baseQuery = query(baseQuery, where('isPremium', '==', true))

  if (!shouldIncludeDrafts) baseQuery = query(baseQuery, where('isDraft', '==', false))

  baseQuery = query(baseQuery, where('isPending', '==', false))
  baseQuery = query(baseQuery, where('isArchived', '==', false))

  const queryWithOptions = formatQueryOptions(baseQuery, options)
  const queryWithFilters = formatQueryFilters(queryWithOptions, filter)

  const querySnapshot = await getDocs(queryWithFilters)
  let finalPosts = formatQuerySnapshot(querySnapshot)

  finalPosts = finalPosts.map((post) => {
    const viewerCategories: string[] = post.viewerCategories ?? []
    const programIds: string[] = post.programIds ?? []
    return {
      ...post,
      combinedCategoriesAndProgramIds: [...viewerCategories, ...programIds],
    }
  })

  return finalPosts
}

export const filterPostsWithTags = async (
  tagGroups: TagGroup[],
  options: Options<Post> = {
    orderDirection: 'desc',
  },
): Promise<Array<Post>> => {
  if (!tagGroups.length) return []

  const baseQuery = query(collection(db, 'posts'))

  const resultPromises = tagGroups.map((tagGroup) => {
    const finalQuery = query(
      baseQuery,
      where(`tagGroups.${tagGroup.id}`, 'array-contains-any', tagGroup.tags),
    )
    const queryWithOptions = formatQueryOptions(finalQuery, options)
    const queryWithFilters = formatQueryFilters(queryWithOptions, 'Workouts')
    return getDocs(queryWithFilters)
  })

  const awaited = await Promise.all(resultPromises)

  const results = awaited.map((resultPromise) => {
    return formatQuerySnapshot(resultPromise)
  })

  // Flattening the arrays and filtering for duplicates
  const finalResults: { ids: string[]; posts: Post[] } = { ids: [], posts: [] }
  results.forEach((queryResult) => {
    queryResult.forEach((post: Post) => {
      if (!finalResults.ids.includes(post.id)) {
        finalResults.ids.push(post.id)
        finalResults.posts.push(post)
      }
    })
  })

  return finalResults.posts
}

/**
 * Fetches the posts as specified in the provided array of IDS.
 * @param postIds - The IDs of the Posts to fetch.
 * @returns An array of Posts.
 */
export const fetchPostsbyIds = async ({ postIds }: { postIds: string[] }): Promise<Array<Post>> => {
  const postDocs = postIds.map((postId) => {
    const docref = doc(db, 'posts', postId)
    return getDoc(docref)
  })
  const postDocsResolve = await Promise.all(postDocs)
  const results = postDocsResolve
    .map((postResolve) => {
      try {
        const finalPost = formatDocSnapshot(postResolve) as Post

        const viewerCategories: string[] = finalPost.viewerCategories ?? []
        const programIds: string[] = finalPost.programIds ?? []

        return {
          ...finalPost,
          combinedCategoriesAndProgramIds: [...viewerCategories, ...programIds],
        }
      } catch (err) {
        return null
      }
    })
    .filter((post) => !!post)

  return results as Post[]
}

export const completePost = async (data: Data) => {
  if (!data || !data?.user || !data?.post) throw new Error('No data passed into completePost')

  const collectionRef = collection(db, 'completions')
  const completionRef = await addDoc(collectionRef, data)
  const newCompletion = await getDoc(completionRef)
  return formatDocSnapshot(newCompletion)
}

export const likePost = async (data: Data) => {
  if (!data || !data?.user || !data?.postId) throw new Error('No data passed into likePost')

  const { user, postId } = data
  const partialUser = createPartialUser(user)
  const likesRef = collection(db, 'posts', postId, 'likes')
  const likeRef = doc(likesRef, user.id)
  const like = await getDoc(likeRef)
  if (like.exists()) {
    // Swallow this error since the action was already performed.
    console.error(`User already liked Post ${postId}`)
    return true
  }
  await setDoc(likeRef, partialUser)
  return true
}

export const dislikePost = async (data: Data) => {
  if (!data || !data?.user || !data?.postId) throw new Error('No data passed into dislikePost')

  const { user, postId } = data
  const likesRef = collection(db, 'posts', postId, 'likes')
  const likeRef = doc(likesRef, user.id)
  await deleteDoc(likeRef)
  return true
}

export const isPostLiked = async (data: Data) => {
  if (!data || !data?.userId || !data?.postId) throw new Error('No data passed into isPostLiked')

  const { userId, postId } = data
  const likeRef = doc(db, 'posts', postId, 'likes', userId)
  const like = await getDoc(likeRef)
  return like.exists()
}
